import type { SVGProps } from 'react';

/**
 * SVG icon library ported from public/legacy-demo.html. Each component
 * accepts standard SVG props (width, height, className, etc.). All
 * stroke their currentColor — set via CSS color on a parent.
 */

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

function svgProps(p: IconProps): SVGProps<SVGSVGElement> {
  const { size = 24, ...rest } = p;
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    ...rest,
  };
}

export function IPiston(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="6" y="3" width="12" height="9" rx="1" />
      <line x1="6" y1="6" x2="18" y2="6" />
      <line x1="6" y1="9" x2="18" y2="9" />
      <path d="M9 12 L9 16 L7 18 L7 21 L17 21 L17 18 L15 16 L15 12" />
    </svg>
  );
}

export function IBlock(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="3" y="6" width="18" height="14" rx="1" />
      <circle cx="8" cy="13" r="2" />
      <circle cx="16" cy="13" r="2" />
      <path d="M3 9 L21 9" />
      <path d="M7 6 L7 4 L17 4 L17 6" />
    </svg>
  );
}

export function IHead(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 8 L21 8 L21 16 L3 16 Z" />
      <circle cx="7" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="17" cy="12" r="1.5" />
      <path d="M3 5 L21 5" />
      <path d="M3 19 L21 19" />
    </svg>
  );
}

export function IIntake(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M5 16 L5 10 L9 6 L15 6 L19 10 L19 16 Z" />
      <circle cx="9" cy="11" r="1.2" />
      <circle cx="15" cy="11" r="1.2" />
      <path d="M5 16 L19 16 L19 19 L5 19 Z" />
    </svg>
  );
}

export function ICover(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 8 Q3 6 5 6 L19 6 Q21 6 21 8 L21 16 L3 16 Z" />
      <path d="M6 10 L18 10" strokeDasharray="2 2" />
    </svg>
  );
}

export function IRod(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M7.5 7.5 L16.5 16.5" />
      <path d="M5 4.5 L7 6.5 M19 17 L17 19" strokeWidth="0.8" />
    </svg>
  );
}

export function ICrank(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="3" y1="12" x2="21" y2="12" />
      <circle cx="8" cy="12" r="2.5" />
      <circle cx="16" cy="12" r="2.5" />
      <line x1="8" y1="9.5" x2="8" y2="6" />
      <line x1="16" y1="14.5" x2="16" y2="18" />
    </svg>
  );
}

export function IFender(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M2 16 Q2 8 8 6 L16 6 Q22 8 22 16 Z" />
      <circle cx="8" cy="16" r="2.5" />
      <circle cx="16" cy="16" r="2.5" />
    </svg>
  );
}

export function IHood(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 16 L4 8 L20 8 L21 16 Z" />
      <rect x="9" y="10" width="6" height="3" fill="currentColor" fillOpacity="0.2" />
      <line x1="3" y1="16" x2="21" y2="16" />
    </svg>
  );
}

export function ITrunk(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 14 L3 8 L21 8 L21 14" />
      <path d="M3 14 L21 14" />
      <circle cx="12" cy="11" r="1" />
    </svg>
  );
}

export function ICarb(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="6" y="4" width="5" height="14" rx="1" />
      <rect x="13" y="4" width="5" height="14" rx="1" />
      <circle cx="8.5" cy="11" r="1.2" />
      <circle cx="15.5" cy="11" r="1.2" />
      <path d="M3 18 L21 18 L21 21 L3 21 Z" />
    </svg>
  );
}

export function ICam(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="2" y1="12" x2="22" y2="12" />
      <ellipse cx="6" cy="12" rx="2" ry="3" />
      <ellipse cx="12" cy="12" rx="2" ry="3" transform="rotate(60 12 12)" />
      <ellipse cx="18" cy="12" rx="2" ry="3" transform="rotate(120 18 12)" />
    </svg>
  );
}

export function IExhaust(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 8 L8 8 L8 12 L13 12 L13 16 L21 16" />
      <circle cx="3" cy="8" r="1" />
      <circle cx="21" cy="16" r="1.5" />
    </svg>
  );
}

export function IInterior(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M5 20 L5 8 Q5 5 8 5 L16 5 Q19 5 19 8 L19 20" />
      <path d="M5 14 L19 14" />
      <path d="M9 5 L9 14" />
    </svg>
  );
}

export function IWing(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 14 L21 14" />
      <path d="M6 14 L6 6 L8 6 L8 14" />
      <path d="M16 14 L16 6 L18 6 L18 14" />
      <path d="M5 6 L19 6" />
    </svg>
  );
}

export function IFastener(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <polygon points="12,3 16,6 16,12 12,15 8,12 8,6" />
      <line x1="12" y1="15" x2="12" y2="21" />
    </svg>
  );
}

const PART_ICONS = {
  'i-piston': IPiston,
  'i-block': IBlock,
  'i-head': IHead,
  'i-intake': IIntake,
  'i-cover': ICover,
  'i-rod': IRod,
  'i-crank': ICrank,
  'i-fender': IFender,
  'i-hood': IHood,
  'i-trunk': ITrunk,
  'i-carb': ICarb,
  'i-cam': ICam,
  'i-exhaust': IExhaust,
  'i-interior': IInterior,
  'i-wing': IWing,
  'i-fastener': IFastener,
} as const;

export type PartIconId = keyof typeof PART_ICONS;

export const PART_ICON_IDS: readonly PartIconId[] = Object.keys(PART_ICONS) as PartIconId[];

interface PartIconProps extends IconProps {
  iconId: string;
  fallback?: PartIconId;
}

export function PartIcon({ iconId, fallback = 'i-piston', ...rest }: PartIconProps) {
  const Component = (PART_ICONS as Record<string, (p: IconProps) => JSX.Element>)[iconId] ?? PART_ICONS[fallback];
  return <Component {...rest} />;
}

// Admin / UI icons
export function ISearch(p: IconProps) {
  return (
    <svg {...svgProps({ ...p, strokeWidth: 2 })}>
      <circle cx="11" cy="11" r="7" />
      <line x1="16" y1="16" x2="21" y2="21" />
    </svg>
  );
}

export function IVehicle(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 14 L3 11 L5 7 L19 7 L21 11 L21 14" />
      <path d="M3 14 L21 14 L21 17 L3 17 Z" />
      <circle cx="7" cy="17" r="1.5" fill="currentColor" />
      <circle cx="17" cy="17" r="1.5" fill="currentColor" />
    </svg>
  );
}

export function ICart(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 4 L5 4 L7 16 L19 16 L21 8 L7 8" />
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="17" cy="20" r="1.5" />
    </svg>
  );
}

export function IDoc(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M5 3 L15 3 L19 7 L19 21 L5 21 Z" />
      <path d="M15 3 L15 7 L19 7" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="16" x2="16" y2="16" />
    </svg>
  );
}

export function IUsers(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20 Q3 14 9 14 Q15 14 15 20" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M21 20 Q21 15 17 15" />
    </svg>
  );
}

export function IBolt(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M13 3 L5 13 L11 13 L9 21 L19 11 L13 11 Z" />
    </svg>
  );
}

export function IChart(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="3" y1="20" x2="21" y2="20" />
      <rect x="5" y="12" width="3" height="8" />
      <rect x="11" y="6" width="3" height="14" />
      <rect x="17" y="9" width="3" height="11" />
    </svg>
  );
}

export function ICog(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3 L12 6 M12 18 L12 21 M3 12 L6 12 M18 12 L21 12 M5.6 5.6 L7.7 7.7 M16.3 16.3 L18.4 18.4 M5.6 18.4 L7.7 16.3 M16.3 7.7 L18.4 5.6" />
    </svg>
  );
}
